type AnyFunction = (...args: any[]) => any;
type Transform<F extends AnyFunction> = (
  result: ReturnType<F>
) => ReturnType<F>;

export type Overridable<F extends AnyFunction> = F & {
  applyOverrides: <T extends F>(
    transform: (result: ReturnType<T>) => ReturnType<T>
  ) => void;
  resetOverrides: () => void;
};

export const makeOverridableFactory = <F extends AnyFunction>(
  factory: F
): Overridable<F> => {
  const overrides: { transform: Transform<F> | null } = { transform: null };

  // Create a base function explicitly typed as (...args: Parameters<F>) => ReturnType<F>
  const base = (...args: Parameters<F>): ReturnType<F> => {
    const original = factory(...args);
    return overrides.transform ? overrides.transform(original) : original;
  };

  // Merge extra methods onto the base function
  return Object.assign(base, {
    applyOverrides: (transform: Transform<F>) => {
      const existing = overrides.transform;
      overrides.transform = existing
        ? (result: ReturnType<F>) => transform(existing(result))
        : transform;
    },
    resetOverrides: () => {
      overrides.transform = null;
    },
  }) as Overridable<F>;
};
